import { createGenerationParams, getTerrainTemplate } from '@/terrain/TerrainTemplates';
import type { TerrainGenerationParams, TerrainTemplateId } from '@/terrain/TerrainTypes';

/**
 * "New site" options and the terrain parameters they produce (spec §6.1).
 *
 * Two starting points, because they answer different questions. A **blank** site is a flat, empty
 * plane — the right canvas when the user is reproducing a real garden and every contour will be
 * placed deliberately. A **generated** site is seeded procedural terrain from one of the templates —
 * the right canvas for exploring, and the one that benefits from a re-roll button.
 *
 * The mapping is a pure function so "what does this dialog actually build" is testable without a
 * renderer.
 */

export type NewSiteKind = 'blank' | 'generated';

/**
 * Templates that generate a **furnished** site — buildings, surfaces and planting — rather than only
 * a heightfield.
 *
 * Terrain-only templates answer "what shape is the ground"; these answer "what is the place". A
 * template called Flat Residential Lot that produced a flat rectangle and nothing else was not a
 * mild omission, it was the whole promise of the name going unmet.
 */
export const FURNISHED_TEMPLATE_IDS: readonly TerrainTemplateId[] = ['flat-residential'];

export function isFurnishedTemplate(id: TerrainTemplateId | undefined): boolean {
  return id !== undefined && FURNISHED_TEMPLATE_IDS.includes(id);
}

/** Whether these options should build a whole site rather than just terrain. */
export function generatesContent(options: NewSiteOptions): boolean {
  return options.kind === 'generated' && isFurnishedTemplate(options.templateId);
}

export interface NewSiteOptions {
  kind: NewSiteKind;
  /** Ignored for a blank site, which is always the blank-custom template. */
  templateId?: TerrainTemplateId;
  /** Noise seed for a generated site. Ignored for a blank site (it has no elevation). */
  seed?: number;
  widthMeters?: number;
  lengthMeters?: number;
}

/** Site dimensions the dialog accepts, in meters. */
export const MIN_SITE_METERS = 5;
export const MAX_SITE_METERS = 200;

/** Templates offered for a generated site — every template except the blank canvas. */
export const GENERATED_TEMPLATE_IDS: readonly TerrainTemplateId[] = [
  'flat-residential',
  'gentle-slope',
  'rolling-landscape',
  'natural-clearing',
];

export const DEFAULT_GENERATED_TEMPLATE_ID: TerrainTemplateId = 'rolling-landscape';

/** A fresh noise seed for the "randomize" control. */
export function randomSiteSeed(): number {
  return Math.floor(Math.random() * 100000) + 1;
}

/** Default options for the dialog: a blank canvas at the blank template's size. */
export function defaultNewSiteOptions(): Required<NewSiteOptions> {
  const blank = getTerrainTemplate('blank-custom').defaults;
  return {
    kind: 'blank',
    templateId: DEFAULT_GENERATED_TEMPLATE_ID,
    seed: randomSiteSeed(),
    widthMeters: blank.widthMeters,
    lengthMeters: blank.lengthMeters,
  };
}

/**
 * Terrain generation parameters for a new site. A blank site zeroes the elevation influence so the
 * ground is dead flat regardless of seed; a generated site keeps its template's character and varies
 * only by seed and extent.
 */
export function newSiteParams(options: NewSiteOptions): TerrainGenerationParams {
  const size = {
    ...(options.widthMeters !== undefined
      ? { widthMeters: clampSize(options.widthMeters) }
      : {}),
    ...(options.lengthMeters !== undefined
      ? { lengthMeters: clampSize(options.lengthMeters) }
      : {}),
  };

  if (options.kind === 'blank') {
    return createGenerationParams('blank-custom', {
      ...size,
      seed: 1,
      elevationAmplitude: 0,
      roughness: 0,
      slopeRiseMeters: 0,
    });
  }

  const templateId = options.templateId ?? DEFAULT_GENERATED_TEMPLATE_ID;
  return createGenerationParams(templateId, {
    ...size,
    seed: Math.max(1, Math.trunc(options.seed ?? randomSiteSeed())),
  });
}

/** Clamp a user-entered dimension into the supported range, falling back on a bad number. */
export function clampSize(meters: number): number {
  if (!Number.isFinite(meters)) return MIN_SITE_METERS;
  return Math.min(MAX_SITE_METERS, Math.max(MIN_SITE_METERS, meters));
}
